import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import CommonLayout from "../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../services/apiServices";
import { getAuthConfig } from "../../services/apiUtils";
import "./RecommendedSongs.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../../pages/common/ConfirmationModal/ConfirmationModal";
import { songImageUrl, extractObjectId } from "../../utils/utils";
import MultiGenreSelect from "../common/MultiGenreSelect";
import PageTargetSelector from "../common/PageTargetSelector";
import usePageTargetSelection from "../../hooks/usePageTargetSelection";
import {
  FaMusic,
  FaSearch,
  FaUser,
  FaCompactDisc,
  FaPlay,
  FaEye,
  FaTrash,
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaStar
} from "react-icons/fa";

const RecommendedSongs = () => {
  const navigate = useNavigate();
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;

  const [searchTerm, setSearchTerm] = useState("");
  const [recommendedSongs, setRecommendedSongs] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [deletingId, setDeletingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [genres, setGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [expandedGenres, setExpandedGenres] = useState({});

  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres(
        { page: 1, limit: 1000 },
        getAuthConfig()
      );
      if (response?.success && Array.isArray(response.data)) {
        setGenres(response.data);
      }
    } catch (error) {
      console.error("Error fetching genres:", error);
    }
  };

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchSongRecommendedList = async (page, pageLimit) => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getSongRecommendedList(
        { page, limit: pageLimit, ...targetParams },
        getAuthConfig()
      );
      const { success, data } = response;

      if (success && Array.isArray(data.songs)) {
        setRecommendedSongs(data.songs);
        setTotalPages(data.totalPages);
        setTotalCount(data.totalCount || data.songs.length);
      } else {
        console.warn("No data found or data is not an array");
      }
    } catch (error) {
      console.error("Error fetching recommended songs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchSong = async (song_name) => {
    if (!song_name.trim()) return;
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getSearchSong(
        { song_name },
        getAuthConfig()
      );
      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
    } catch (error) {
      console.error("Error fetching search song:", error);
      toast.error("Failed to fetch search song");
    } finally {
      setLoading(false);
    }
  };

  const addRecommendedSong = async (song_id) => {
    const genreArray = selectedGenres[song_id] || [];
    const genre = genreArray.length > 0 ? genreArray.join(", ") : null;
    setLoading(true);
    try {
      const response = await allAPiServicesCall.addRecommendedSong(
        { song_id, genre, ...targetParams },
        getAuthConfig()
      );

      if (response && response.success) {
        toast.success("Song added to recommended list.");
        setSearchTerm("");
        setSelectedGenres({});
        fetchSongRecommendedList(currentPage, limit);
      } else {
        const errorMessage = response
          ? response.message
          : "Failed to add song. Please try again.";
        toast.error("Failed to add song: " + errorMessage);
      }
    } catch (error) {
      console.error("Error adding recommended song:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to add recommended song.";
      toast.error("Failed to add recommended song: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGenreSelect = (songId, genreArray) => {
    setSelectedGenres((prev) => ({
      ...prev,
      [songId]: genreArray,
    }));
  };

  const handleOnDeleteClick = (songId) => {
    setDeletingId(songId);
    setShowModal(true);
  };

  const handleConfirmDelete = async (songId) => {
    setDeletingId(songId);
    setLoading(true);
    try {
      const payload = { id: songId };
      const response = await allAPiServicesCall.deleteRecommendedSong(
        payload,
        getAuthConfig()
      );

      if (response && response.status === 200) {
        setShowModal(false);
        toast.success("Song deleted successfully!");
        fetchSongRecommendedList(currentPage, limit);
      } else {
        toast.error("Failed to delete song!");
      }
    } catch (error) {
      console.error("There was an error deleting the song!", error);
      toast.error("Failed to delete song!");
    } finally {
      setLoading(false);
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      fetchSongRecommendedList(currentPage, limit);
      setSearchResults([]);
    }
  }, [searchTerm, currentPage, limit, targetParams.pageType, targetParams.genreSlug]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 2000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedTerm) {
      fetchSearchSong(debouncedTerm);
    }
  }, [debouncedTerm]);

  const handleSearchTermChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  return (
    <CommonLayout>
      <div className="recommended-songs-page">
        {/* Header Section */}
        <div className="recommended-header">
          <div className="recommended-header-content">
            <div className="recommended-header-icon">
              <FaStar />
            </div>
            <div className="recommended-header-text">
              <h1 className="recommended-title">Recommended Songs</h1>
              <p className="recommended-subtitle">
                Curate and manage featured songs for your users
              </p>
            </div>
          </div>
          <div className="recommended-stats">
            <div className="recommended-stat-card">
              <FaMusic className="recommended-stat-icon" />
              <div className="recommended-stat-info">
                <span className="recommended-stat-value">{totalCount}</span>
                <span className="recommended-stat-label">Total Songs</span>
              </div>
            </div>
          </div>
        </div>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="Switch between Homepage and Genre targets before curating recommended songs."
        />

        {/* Search & Filter Controls */}
        <div className="recommended-controls">
          <div className="recommended-search-wrapper">
            <FaSearch className="recommended-search-icon" />
            <input
              type="text"
              className="recommended-search-input"
              placeholder="Search for a song to add to recommendations..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="recommended-loading">
            <div className="recommended-loading-spinner">
              <Spinner animation="border" />
            </div>
            <p className="recommended-loading-text">Loading songs...</p>
          </div>
        ) : searchResults.length > 0 ? (
          <>
            {/* Search Results Section */}
            <div className="recommended-section-header">
              <div className="recommended-section-icon recommended-section-icon-search">
                <FaSearch />
              </div>
              <h2 className="recommended-section-title">Search Results</h2>
              <span className="recommended-section-count">
                {searchResults.length} found
              </span>
            </div>
            <div className="recommended-grid">
              {searchResults.map((song, index) => (
                <div
                  key={index}
                  className="recommended-card recommended-card-search"
                >
                  <div className="recommended-card-image-wrapper">
                    <img
                      src={songImageUrl(song)}
                      alt={song.song_name}
                      className="recommended-card-image"
                    />
                    <div className="recommended-card-overlay">
                      <div className="recommended-card-play">
                        <FaPlay />
                      </div>
                    </div>
                    <div className="recommended-card-badge recommended-card-badge-search">
                      <FaSearch /> Result
                    </div>
                  </div>
                  <div className="recommended-card-content">
                    <h3 className="recommended-card-title">{song.song_name}</h3>
                    <div className="recommended-card-meta">
                      <div className="recommended-card-meta-item">
                        <FaUser className="recommended-card-meta-icon" />
                        <span className="recommended-card-meta-value">
                          {song?.artist?.artist_name || "Unknown Artist"}
                        </span>
                      </div>
                    </div>
                    <div className="recommended-genre-select">
                      <MultiGenreSelect
                        genres={genres}
                        selectedGenres={selectedGenres[song.song_id] || []}
                        onChange={(genreArray) =>
                          handleGenreSelect(song.song_id, genreArray)
                        }
                        placeholder="Select Genres (Optional)"
                        size="sm"
                      />
                    </div>
                    <div className="recommended-card-actions">
                      <button
                        className="recommended-action-btn recommended-action-btn-add"
                        onClick={() => addRecommendedSong(song.song_id)}
                      >
                        <FaPlus /> Add to Recommended
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : recommendedSongs.length === 0 ? (
          <div className="recommended-empty">
            <div className="recommended-empty-icon">
              <FaMusic />
            </div>
            <h3 className="recommended-empty-title">No Recommended Songs</h3>
            <p className="recommended-empty-text">
              Search for songs above to add them to your recommended list
            </p>
          </div>
        ) : (
          <>
            {/* Recommended Songs Grid */}
            <div className="recommended-grid">
              {recommendedSongs
                .filter((song) =>
                  song.song_name
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                )
                .map((song, index) => (
                  <div key={index} className="recommended-card">
                    <div className="recommended-card-image-wrapper">
                      <img
                        src={songImageUrl(song)}
                        alt={song.song_name}
                        className="recommended-card-image"
                      />
                      <div className="recommended-card-overlay">
                        <div className="recommended-card-play">
                          <FaPlay />
                        </div>
                      </div>
                      <div className="recommended-card-badge">
                        <FaStar /> Featured
                      </div>
                    </div>
                    <div className="recommended-card-content">
                      <h3 className="recommended-card-title">
                        {song.song_name}
                      </h3>
                      <div className="recommended-card-meta">
                        <div className="recommended-card-meta-item">
                          <FaUser className="recommended-card-meta-icon" />
                          <span className="recommended-card-meta-value">
                            {song.artist?.artist_name || "Unknown Artist"}
                          </span>
                        </div>
                        <div className="recommended-card-meta-item">
                          <FaCompactDisc className="recommended-card-meta-icon" />
                          <span className="recommended-card-meta-value">
                            {song.album?.album_name || "No Album"}
                          </span>
                        </div>
                      </div>
                      {song.genre && (() => {
                        const genreList = song.genre.split(", ").map(g => g.trim());
                        const isExpanded = expandedGenres[song._id];
                        const visibleGenres = isExpanded ? genreList : genreList.slice(0, 4);
                        const hasMore = genreList.length > 4;

                        return (
                          <div className="recommended-card-genres">
                            {visibleGenres.map((g, i) => (
                              <span key={i} className="recommended-card-genre">
                                {g}
                              </span>
                            ))}
                            {hasMore && (
                              <button
                                className="recommended-genre-toggle"
                                onClick={() => setExpandedGenres(prev => ({
                                  ...prev,
                                  [song._id]: !prev[song._id]
                                }))}
                              >
                                {isExpanded ? "Show less" : `+${genreList.length - 4} more`}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      <div className="recommended-card-actions">
                        <button
                          className="recommended-action-btn recommended-action-btn-view"
                          onClick={() => navigate(`/lyrics/${song.song_id}`)}
                        >
                          <FaEye /> View
                        </button>
                        <button
                          className="recommended-action-btn recommended-action-btn-delete"
                          onClick={() =>
                            handleOnDeleteClick(extractObjectId(song._id))
                          }
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="recommended-pagination">
                <button
                  className="recommended-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="recommended-pagination-info">
                  <span className="recommended-pagination-current">
                    {currentPage}
                  </span>
                  <span className="recommended-pagination-separator">/</span>
                  <span className="recommended-pagination-total">
                    {totalPages}
                  </span>
                </div>
                <button
                  className="recommended-pagination-btn"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onConfirm={() => handleConfirmDelete(deletingId)}
        message="Are you sure you want to delete this song from recommendations?"
      />
    </CommonLayout>
  );
};

export default RecommendedSongs;
